import { useEffect, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { ButtonSize } from "../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import {
  formatDisplayDate,
  parseSortableDate,
} from "../../../../classes/utils/DateUtils";
import { isRequestCancelled } from "../../../../classes/utils/RequestUtils";
import IconButton from "../../../../components/iconButton/IconButton";
import { PosturalImageThumbnail } from "../../../../components/posturalImageThumbnail/PosturalImageThumbnail";
import { getPatientMorphologyPicture } from "../../../../services/ImageService";
import DeleteMorphologyImageModal from "./DeleteMorphologyImageModal";

const MorphologyGallery = ({
  images,
  onImageClick,
  canDelete,
  patientId,
  onImageDeleted,
}) => {
  const { showPopup } = useAppContext();
  const { dateSettings } = useUser();
  const [imagesWithUrls, setImagesWithUrls] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    const urlsToCleanup = [];

    const fetchImages = async () => {
      try {
        setImagesWithUrls([]);

        if (!images?.length) {
          return;
        }

        const updatedImages = await Promise.all(
          images.map(async image => {
            try {
              const response = await getPatientMorphologyPicture(
                patientId,
                image.title,
                { signal: abortController.signal }
              );

              if (!isMounted) return null;

              if (!response?.data) {
                return null;
              }

              const objectUrl = URL.createObjectURL(response.data);
              urlsToCleanup.push(objectUrl);
              const parsedDate =
                parseSortableDate(image.date) || new Date(image.date);
              const timestamp = parsedDate?.getTime ? parsedDate.getTime() : 0;
              const formattedDate =
                formatDisplayDate(parsedDate, {
                  locale: dateSettings?.locale,
                  format: dateSettings?.format,
                }) || "-";

              return {
                ...image,
                url: objectUrl, // PosturalImageThumbnail expects 'url'
                objectUrl, // Keep for backward compatibility
                timestamp,
                formattedDate,
              };
            } catch (error) {
              if (!isRequestCancelled(error)) {
                // Individual image load failure — exclude it from the gallery
              }
              return null;
            }
          })
        );

        if (isMounted) {
          const validImages = updatedImages.filter(Boolean);
          setImagesWithUrls(validImages);
        }
      } catch (error) {
        if (isMounted && !isRequestCancelled(error)) {
          setImagesWithUrls([]);
        }
      }
    };

    fetchImages();

    return () => {
      isMounted = false;
      abortController.abort();

      urlsToCleanup.forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [images, patientId]);

  if (!imagesWithUrls?.length) {
    return null;
  }

  // Group images by date
  const groupedImages = imagesWithUrls.reduce((groups, image) => {
    const date = image.formattedDate;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(image);
    return groups;
  }, {});

  // Sort dates in descending order (most recent first) using timestamps
  const sortedDates = Object.keys(groupedImages).sort((a, b) => {
    const tsA = groupedImages[a]?.[0]?.timestamp || 0;
    const tsB = groupedImages[b]?.[0]?.timestamp || 0;
    return tsB - tsA;
  });

  return (
    <div className="image-gallery">
      {sortedDates.map(date => (
        <div key={date} className="date-section">
          <div className="date-header">
            <div className="App-text-large date-section-title">{date}</div>
          </div>
          <div className="images-grid-wrapper">
            <div className="images-grid">
              {groupedImages[date].map(image => (
                <div
                  key={`${image.title}-${image.date}`}
                  className="image-morphology-thumbnail-wrapper"
                  style={{ position: "relative" }}
                >
                  <PosturalImageThumbnail
                    image={image}
                    isSelected={false}
                    onClick={() => onImageClick(image)}
                  />
                  {canDelete && (
                    <div
                      className="thumbnail-delete-btn"
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        zIndex: 2,
                      }}
                    >
                      <IconButton
                        icon={IconsEnum.DELETE}
                        size={ButtonSize.SMALL}
                        noBgColor
                        title="Delete Image"
                        onClick={e => {
                          e.stopPropagation();
                          showPopup(
                            <DeleteMorphologyImageModal
                              patientId={patientId}
                              image={image}
                              onImageDeleted={onImageDeleted}
                            />
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MorphologyGallery;
